import React, { useState, useEffect } from 'react';
import OlcButton from '~/components/General/OlcButton';

// Hooks
import { useDispatch } from 'react-redux';
import { isAdmin } from '~/hooks/permissions';
import DoneIcon from '@mui/icons-material/Done';
// Actions
import {
  fetchGeneralSetting,
  updateGeneralSetting,
} from '../../../../redux/actions/general-actions';
import { failure, success } from '../../../../redux/actions/snackbar-actions';

//Utils
import { MESSAGES } from '~/utils/message';

import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
  Link,
  CircularProgress,
} from '@mui/material';
import './styles.scss';
import { CopyIcon } from '~/assets/images';

import {
  ADDRESS_STRICTNESS_LEARN_URL,
  GENERAL_SETTINGS_LEARN_URL,
  RETURN_ADDRESS_LEARN_URL,
} from '~/utils/constants';

const Section = ({
  title,
  description,
  children,
  showLearnMore,
  showMoreLink,
  containerStyle = '',
}) => (
  <Box>
    <Box style={{ padding: '8px 20px' }}>
      <Box className={`cpSectionGeneral ${containerStyle}`}>
        <Box>
          <Typography className="cpSectionTitle">{title}</Typography>
          {description ? (
            <Typography className="cpSectionDesc">
              {description}
              {showLearnMore && showMoreLink && (
                <Link
                  href={showMoreLink}
                  className="cpLearnMore"
                  target="_blank"
                  sx={{ ml: '5px' }}
                >
                  {showLearnMore}
                </Link>
              )}
            </Typography>
          ) : null}
        </Box>
        <Box className="cpSectionBody">{children}</Box>
      </Box>
    </Box>
  </Box>
);

const General = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);

  // API-related state variables
  const [addressStrictness, setAddressStrictness] = useState(null);
  const [variableStrictness, setVariableStrictness] = useState(null);
  const [propertyAddressStrictness, setPropertyAddressStrictness] = useState(null);
  const [returnAddressName, setReturnAddressName] = useState(null);
  const [returnAddressLastName, setReturnAddressLastName] = useState(null);
  const [returnAddressCompany, setReturnAddressCompany] = useState(null);
  const [returnAddressAddress, setReturnAddressAddress] = useState(null);

  const dispatch = useDispatch();

  const getGeneralSetting = async () => {
    setLoading(true);
    const settings = await dispatch(fetchGeneralSetting());
    if (settings.status === 200) {
      setAddressStrictness(settings.data.data.addressStrictness);
      setVariableStrictness(settings.data.data.mergeVariableStrictness);
      setPropertyAddressStrictness(settings.data.data.propertyAddressStrictness);
      setReturnAddressName(settings.data.data.returnAddressSettings.firstName);
      setReturnAddressLastName(settings.data.data.returnAddressSettings.lastName);
      setReturnAddressCompany(settings.data.data.returnAddressSettings.companyName);
      setReturnAddressAddress(settings.data.data.returnAddressSettings.fullAddress);
    }
    setLoading(false);
  };

  const updateSetting = async () => {
    let payload = {
      addressStrictness: addressStrictness,
      mergeVariableStrictness: variableStrictness,
      propertyAddressStrictness: propertyAddressStrictness,
      returnAddressSettings: {
        firstName: returnAddressName,
        lastName: returnAddressLastName,
        companyName: returnAddressCompany,
        fullAddress: returnAddressAddress,
      },
    };
    setLoading(true);
    const update = await dispatch(updateGeneralSetting(payload));
    if (update.status === 200) {
      setIsEditMode(false);
      dispatch(success(update.data.message));
      await dispatch(fetchGeneralSetting());
    } else if (update.status !== 200) {
      dispatch(failure(MESSAGES.SETTINGS.GENERAL_SETTINGS.RA_ERROR_MESSAGE));
    }
    setLoading(false);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleSave = () => {
    updateSetting();
  };

  const handleCancel = () => {
    setIsEditMode(false);
    // Reset to original values by fetching fresh data
    getGeneralSetting();
  };

  useEffect(() => {
    getGeneralSetting();
  }, []);

  return (
    <Box className="cpContent">
      <Box className="cpSectionGeneral generalHeader" sx={{ p: 0 }}>
        <Box
          className="cpActionsRow"
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: '20px' }}
        >
          <OlcButton
            variant="secondary"
            className="cpSecondaryBtn"
            onClick={() => (isEditMode ? handleCancel() : handleEdit())}
            disabled={loading}
          >
            {isEditMode ? (
              'Cancel'
            ) : (
              'Edit'
            )}
          </OlcButton>
          {isEditMode && (
            <OlcButton
              variant="primary"
              className="cpPrimaryBtn"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress
                  sx={{
                    color: 'white',
                    width: '20px !important',
                    height: '20px !important',
                  }}
                />
              ) : (
                'Save Changes'
              )}
            </OlcButton>
          )}
        </Box>
      </Box>
      <Box style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '20px' }}>
        <Section
          title={MESSAGES.SETTINGS.GENERAL_SETTINGS.ADDRESS_STRICT_LABEL}
          description={MESSAGES.SETTINGS.GENERAL_SETTINGS.ADDRESS_STRICT_DESCRIPTION}
          showLearnMore={MESSAGES.SETTINGS.GENERAL_SETTINGS.LEARN_TITLE}
          showMoreLink={ADDRESS_STRICTNESS_LEARN_URL}
        >
          <Box className="cpChecksRow ">
            <FormControlLabel
              className="cpGeneralCheckbox"
              control={
                <Box className="checkboxRow ">
                  <Checkbox
                    sx={{ opacity: !isEditMode || loading ? '.4' : '1' }}
                    checked={addressStrictness === 'Strict'}
                    className="bulkCheck newbulkCheck checkBoxSm"
                    icon={<Box className="bulkNotchecked" />}
                    checkedIcon={<DoneIcon />}
                    disableRipple={false}
                    disabled={!isEditMode || loading}
                    onChange={() => setAddressStrictness('Strict')}
                  />
                </Box>
              }
              label={
                <span className="cpCheckLabel">
                  <span className="boldTxt">Strict</span> (only send to verified addresses)
                </span>
              }
            />
            <FormControlLabel
              className="cpGeneralCheckbox"
              control={
                <Box className="checkboxRow">
                  <Checkbox
                    sx={{ opacity: !isEditMode || loading ? '.4' : '1' }}
                    checked={addressStrictness === 'Normal'}
                    className="bulkCheck newbulkCheck checkBoxSm"
                    icon={<Box className="bulkNotchecked" />}
                    checkedIcon={<DoneIcon />}
                    disableRipple={false}
                    disabled={!isEditMode || loading}
                    onChange={() => setAddressStrictness('Normal')}
                  />
                </Box>
              }
              label={
                <span className="cpCheckLabel">
                  <span className="boldTxt">Normal</span> (send to verified and corrected addresses
                  using USPS - NCOA)
                </span>
              }
            />
          </Box>
        </Section>

        <Divider />

        <Section
          title={MESSAGES.SETTINGS.GENERAL_SETTINGS.RETURN_ADDRESS_NAME_LABEL}
          description={MESSAGES.SETTINGS.GENERAL_SETTINGS.RETURN_ADDRESS_DESCRIPTION}
          showLearnMore={MESSAGES.SETTINGS.GENERAL_SETTINGS.LEARN_TITLE}
          showMoreLink={RETURN_ADDRESS_LEARN_URL}
        >
          <Box className="cpChecksGrid">
            <Box className="cpChecksRow cpChecksRow2">
              <Box>
                <FormControlLabel
                  className="cpGeneralCheckbox cpMiddleField"
                  control={
                    <Box className="checkboxRow">
                      <Checkbox
                        sx={{ opacity: !isEditMode || loading ? '.4' : '1' }}
                        checked={returnAddressName === true}
                        className="bulkCheck newbulkCheck checkBoxSm"
                        icon={<Box className="bulkNotchecked" />}
                        checkedIcon={<DoneIcon />}
                        disableRipple={false}
                        disabled={!isEditMode || loading}
                        onChange={(e) => setReturnAddressName(e.target.checked)}
                      />
                    </Box>
                  }
                  label={<span className="boldTxt">First Name</span>}
                />
              </Box>
              <Box>
                <FormControlLabel
                  className="cpGeneralCheckbox"
                  control={
                    <Box className="checkboxRow">
                      <Checkbox
                        sx={{ opacity: !isEditMode || loading ? '.4' : '1' }}
                        checked={returnAddressLastName === true}
                        className="bulkCheck newbulkCheck checkBoxSm"
                        icon={<Box className="bulkNotchecked" />}
                        checkedIcon={<DoneIcon />}
                        disableRipple={false}
                        disabled={!isEditMode || loading}
                        onChange={(e) => setReturnAddressLastName(e.target.checked)}
                      />
                    </Box>
                  }
                  label={<span className="boldTxt">Last Name</span>}
                />
              </Box>
            </Box>
            <Box className="cpChecksRow cpChecksRow2" sx={{ marginTop: '12px' }}>
              <Box>
                <FormControlLabel
                  className="cpGeneralCheckbox cpMiddleField"
                  control={
                    <Box className="checkboxRow">
                      <Checkbox
                        sx={{ opacity: !isEditMode || loading ? '.4' : '1' }}
                        checked={returnAddressCompany === true}
                        className="bulkCheck newbulkCheck checkBoxSm"
                        icon={<Box className="bulkNotchecked" />}
                        checkedIcon={<DoneIcon />}
                        disableRipple={false}
                        disabled={!isEditMode || loading}
                        onChange={(e) => setReturnAddressCompany(e.target.checked)}
                      />
                    </Box>
                  }
                  label={<span className="boldTxt">Company Name</span>}
                />
              </Box>
              <Box>
                <FormControlLabel
                  className="cpGeneralCheckbox"
                  control={
                    <Box className="checkboxRow">
                      <Checkbox
                        sx={{ opacity: !isEditMode || loading ? '.4' : '1' }}
                        checked={returnAddressAddress === true}
                        className="bulkCheck newbulkCheck checkBoxSm"
                        icon={<Box className="bulkNotchecked" />}
                        checkedIcon={<DoneIcon />}
                        disableRipple={false}
                        disabled={!isEditMode || loading}
                        onChange={(e) => setReturnAddressAddress(e.target.checked)}
                      />
                    </Box>
                  }
                  label={<span className="boldTxt">Full Address</span>}
                />
              </Box>
            </Box>
          </Box>
        </Section>

        <Divider />

        <Section
          title={MESSAGES.SETTINGS.GENERAL_SETTINGS.PROPERTY_ADDRESS_LINKING}
          description={MESSAGES.SETTINGS.GENERAL_SETTINGS.PROPERTY_ADDRESS_LINKING_DESCRIPTION}
          showLearnMore={MESSAGES.SETTINGS.GENERAL_SETTINGS.LEARN_TITLE}
          showMoreLink={GENERAL_SETTINGS_LEARN_URL}
          containerStyle="lastEle"
        >
          <Box className="cpChecksRow">
            <FormControlLabel
              className="cpGeneralCheckbox"
              control={
                <Box className="checkboxRow">
                  <Checkbox
                    sx={{ opacity: !isEditMode || loading ? '.4' : '1' }}
                    checked={propertyAddressStrictness === 'Include'}
                    className="bulkCheck newbulkCheck checkBoxSm"
                    icon={<Box className="bulkNotchecked" />}
                    checkedIcon={<DoneIcon />}
                    disableRipple={false}
                    disabled={!isEditMode || loading}
                    onChange={(e) =>
                      setPropertyAddressStrictness(e.target.checked ? 'Include' : 'Exclude')
                    }
                  />
                </Box>
              }
              label={<span className="boldTxt">Enable</span>}
            />
          </Box>
        </Section>

        <Divider className="lastGeneralDivider" style={{ marginBottom: '24px' }} />
      </Box>
    </Box>
  );
};

export default General;
